import { EventClipInfo, getFacilityEventTypeDisplayName, getTargetStatusDisplayName, getTargetTypeDisplayName } from "@app/shared";
import { useEffect, useRef, useState } from "react";
import { MediaNoticeList } from "../shared/MediaNoticeList";
import { useSearchParams } from "react-router-dom";
import { getPublicClipInfo } from "../../helpers/clipInfoHelpers";
import { useFetch } from "../../hooks";
import { NoAuthAccountLayout } from "../layouts";
import { DataInfoCard, Icons, ImageLightbox, PageContainer } from "../shared";

const M_S_TO_MPH = 2.237;
const POLL_MS = 8000;
const MAX_POLLS = 60;

export const ClipViewer: React.FC = () => {
    const [params] = useSearchParams();
    const token = params.get("token") ?? undefined;

    // Anonymous on purpose — see getPublicClipInfo. The generated client's credentialed request is
    // refused by the browser against this endpoint's wildcard CORS origin.
    const [clip, error, { isLoading, refreshData }] = useFetch(() => getPublicClipInfo(token), [token]);

    const refreshRef = useRef(refreshData);
    refreshRef.current = refreshData;
    const statusRef = useRef<string | undefined>(clip?.status);
    statusRef.current = clip?.status;

    useEffect(() => {
        if (!token) return;
        let count = 0;
        const id = setInterval(() => {
            if (statusRef.current !== "pending" || count >= MAX_POLLS) {
                clearInterval(id);
                return;
            }
            count++;
            refreshRef.current();
        }, POLL_MS);
        return () => clearInterval(id);
    }, [token]);

    return (
        <NoAuthAccountLayout>
            <ClipBody token={token} clip={clip} isLoading={isLoading} error={error} onRetry={refreshData} />
        </NoAuthAccountLayout>
    );
};

const Spinner = () => (
    <div
        role="status"
        aria-label="Loading"
        className="mx-auto my-4 h-8 w-8 animate-spin rounded-full border-4 border-separators-page border-t-transparent"
    />
);

const Centered: React.FC<React.PropsWithChildren> = ({ children }) => (
    <div className="py-10 text-center text-neutral-900">{children}</div>
);

type ClipBodyProps = {
    token?: string;
    clip: EventClipInfo | undefined;
    isLoading: boolean;
    error: unknown;
    onRetry: () => void;
};

const ClipBody: React.FC<ClipBodyProps> = ({ token, clip, isLoading, error, onRetry }) => {
    const [showLightbox, setShowLightbox] = useState(false);

    if (!token) return <Centered>This clip link is invalid.</Centered>;
    if (isLoading && !clip) return <Centered><Spinner />Loading…</Centered>;

    if (error && !clip) {
        return (
            <Centered>
                <p>We couldn’t load this clip right now.</p>
                <button onClick={onRetry} className="mt-3 min-h-[44px] rounded bg-brand-primary px-4 py-2.5 text-white hover:opacity-90">
                    Try again
                </button>
            </Centered>
        );
    }

    if (!clip || clip.status === "invalid") return <Centered>This clip link is invalid.</Centered>;
    if (clip.status === "expired") {
        return (
            <Centered>
                <p>This clip is no longer available.</p>
                <p className="mt-2 text-sm text-muted-body-text">
                    Clips expire after their retention period. Contact your administrator if you still need it.
                </p>
            </Centered>
        );
    }

    const ready = clip.status === "ready" && !!clip.videoUrl;
    const unavailable = clip.status === "unavailable";
    const stillAlt = `Still image of ${clip.movementLabel ?? "the event"}${clip.facilityName ? ` at ${clip.facilityName}` : ""}`;
    const still = clip.photoUrl ? (
        <img src={clip.photoUrl} alt={stillAlt} className="mx-auto w-full max-h-[50vh] rounded object-contain" onError={(e) => (e.currentTarget.style.display = "none")} />
    ) : null;

    return (
        <div>
            <Header clip={clip} />

            <PageContainer Icon={Icons.Video} title="Event Clip">
                {/* Quality caveats (approximate window, gaps, partial) — shown above the clip so the
                    viewer knows what they're looking at; the server only ever sends user-safe text. */}
                <MediaNoticeList notices={clip.notices} className="mb-3 rounded border border-amber-200 bg-amber-50 p-3" />

                {ready ? (
                    <video src={clip.videoUrl} poster={clip.photoUrl ?? undefined} controls playsInline className="mx-auto w-full max-h-[60vh] rounded bg-black" />
                ) : unavailable ? (
                    <div className="space-y-3">
                        {still}
                        <div className="rounded bg-neutral-100 px-4 py-3 text-center text-muted-body-text">
                            This clip could not be produced for the requested moment.
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {still}
                        <div aria-live="polite" className="flex items-center justify-center gap-3 rounded bg-neutral-100 py-3 text-muted-body-text">
                            <Spinner />
                            <span>Your video is processing — this page will update automatically.</span>
                        </div>
                    </div>
                )}
            </PageContainer>

            {ready && clip.photoUrl && (
                <PageContainer Icon={Icons.Photo} title="Still">
                    <img
                        src={clip.photoUrl}
                        alt={stillAlt}
                        title="Click to enlarge"
                        className="mx-auto max-h-[50vh] cursor-zoom-in rounded object-contain"
                        onClick={() => setShowLightbox(true)}
                        onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                </PageContainer>
            )}

            <div className="grid gap-4 md:grid-cols-2">
                {clip.targetType != null && (
                    <DataInfoCard Icon={Icons.Truck} title="Type">{getTargetTypeDisplayName(clip.targetType)}</DataInfoCard>
                )}
                {clip.targetStatus != null && (
                    <DataInfoCard Icon={Icons.Parking} title="Status">{getTargetStatusDisplayName(clip.targetStatus)}</DataInfoCard>
                )}
                {(clip.width != null || clip.length != null) && (
                    <DataInfoCard Icon={Icons.Ruler} title="Size">{clip.width?.toFixed(1)}, {clip.length?.toFixed(1)} m</DataInfoCard>
                )}
                {clip.countAdjustment != null && (
                    <DataInfoCard Icon={Icons.StopWatch} title="Count Adjustment">{clip.countAdjustment}</DataInfoCard>
                )}
                {clip.maxVelocity != null && (
                    <DataInfoCard Icon={Icons.StopWatch} title="Max Speed">{Math.round(clip.maxVelocity * M_S_TO_MPH)} mph</DataInfoCard>
                )}
            </div>

            {ready && (
                <div className="pt-4 text-center">
                    <a
                        href={clip.videoUrl}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex min-h-[44px] items-center gap-2 rounded bg-brand-primary px-5 py-2.5 text-white hover:opacity-90"
                    >
                        <Icons.Video className="h-4 w-4" /> Download clip
                    </a>
                </div>
            )}

            <p className="pt-4 text-center text-xs text-muted-body-text">
                Shared securely via Omnisight. This is a private link — please don’t forward it.
            </p>

            <ImageLightbox show={showLightbox} src={clip.photoUrl ?? undefined} alt={stillAlt} onClose={() => setShowLightbox(false)} />
        </div>
    );
};

const Header: React.FC<{ clip: EventClipInfo }> = ({ clip }) => {
    const kind = clip.eventType != null ? getFacilityEventTypeDisplayName(clip.eventType) : undefined;
    const when = clip.dateCreated ? new Date(clip.dateCreated).toLocaleString(undefined, { timeZoneName: "short" }) : undefined;
    return (
        <div className="pb-1">
            {kind && <p className="text-xs font-semibold uppercase tracking-wide text-muted-body-text">{kind}</p>}
            <h1 className="text-xl font-semibold text-neutral-900">{clip.movementLabel ?? "Event clip"}</h1>
            <p className="text-sm text-muted-body-text">{[clip.facilityName, clip.deviceName, when].filter(Boolean).join(" • ")}</p>
        </div>
    );
};
